import type {MithraMaterializedApi} from "../../services/MithraMaterializedApi";
import type {ApprovalStore} from "../ApprovalStore";
import {forkJoin, from, mergeMap, tap} from "rxjs";
import {makeAutoObservable, runInAction} from "mobx";
import {ApprovalStatusEnum} from "../../services/classes/AiClasses";
import type {BagStore} from "../BagStore";
import {TaxonomyCategoryProvider} from "../TaxonomyCategoryProvider";
import {routes} from "../../routing/routes";
import type AuthStore from "../../authentication/AuthStore";
import type ProfileStore from "../ProfileStore";
import {CategorizationReviewFeedbackDelegate} from "./CategorizationReviewFeedbackDelegate";
import type {CategorizationReviewStore} from "../categorization-review/CategorizationReviewStore";
import type {NavigateFunction} from "react-router";

/**
 * The Approval Store delegate for Categorization
 */
export class CategorizationApprovalDelegate {
    readonly reviewFeedback: CategorizationReviewFeedbackDelegate;

    /**
     * Used for reloading data in the app after approval
     *
     * This link is during the instantiation of the categorizationStore, as both depend on each-other.
     * We can consider moving this link to a separate flow store to circumvent this problem.
     */
    categorizationStore: CategorizationReviewStore = undefined as any;

    // noinspection JSUnusedLocalSymbols
    constructor(
        private approvalStore: ApprovalStore,
        private auth: AuthStore,
        private bagStore: BagStore,
        private profileStore: ProfileStore,
        private api: MithraMaterializedApi,
    ) {
        makeAutoObservable(this)
        this.reviewFeedback = new CategorizationReviewFeedbackDelegate(this.api);
        this.categories = new TaxonomyCategoryProvider(this.profileStore, this.api);
    }

    /**
     * @deprecated Feedback is not allowed to change the categories anymore so this should not be needed
     */
    readonly categories: TaxonomyCategoryProvider;

    makeRetrieveApprovalRequest(approvalId: number) {
        return from(this.api.getCategorizationApprovalRequest(approvalId)).pipe(
            tap(r => {
                console.log('makeRetrieveApprovalRequest', r.data.current_status.status)
                this.approvalStore.setApproval(r.data);
            }),
            tap(r => {
                // Update the categories of the categorization feedback screen
                // Note: There is some unwanted subscription management here

                const taxonomyId = r.data.baseline_taxonomy;
                if (taxonomyId === null) {
                    this.categories.arrest('Categorization approval cannot be reviewed as taxonomy is missing');
                } else {
                    this.categories.fillCategoriesFromTaxonomy(taxonomyId);
                }
            })
        );
    }

    applyApproval(navigate: NavigateFunction) {
        const approvalId = this.approvalStore.approval?.id;
        if (!approvalId) return

        const update = from(this.api.applyCategorizationApproval(approvalId, ApprovalStatusEnum.APPROVED));

        const request = update.pipe(
            mergeMap(() => forkJoin([
                this.approvalStore.fetchAll(),
                this.makeRetrieveApprovalRequest(approvalId),
            ])),
            tap(([_, r]) => runInAction(() => {
                this.approvalStore.isAcceptApprovalDialogOpen = false;

                if (!ApprovalStatusEnum.statusIsOpenForApprover(r.data.current_status.status)) {
                    navigate(routes.approval);
                }
            })),
        )

        this.approvalStore.approvalLoader.request(request);
    }

    loadStats() {
        const id = this.approvalStore.approval?.id;
        if (!id) {
            this.reviewFeedback.stats.cleanup();
        } else {
            this.reviewFeedback.stats.request(id)
        }
    }
}
